<template>
  <div class="tax-rate-manage-page">
    <header class="console-page-head">
      <div class="console-page-copy">
        <p class="console-kicker">설정</p>
        <h2 class="console-title">요율·최저시급</h2>
        <p class="console-desc">연도별 최저시급과 4대보험·인건비 요율입니다. 임금과 예산 계산에 이 값이 쓰입니다.</p>
      </div>
    </header>

    <section class="tax-rate-manage-card">
      <div class="tax-rate-manage-toolbar">
        <label class="tax-rate-manage-label" for="tax-rate-year">편집 연도</label>
        <select
          id="tax-rate-year"
          v-model.number="editYear"
          class="tax-rate-manage-select"
          :disabled="loading || saving"
          @change="load"
        >
          <option v-for="year in yearItems" :key="year" :value="year">{{ year }}년</option>
        </select>
        <button type="button" class="tax-rate-manage-btn" :disabled="loading || saving" @click="load">
          새로고침
        </button>
      </div>

      <p v-if="missing" class="tax-rate-manage-note">
        {{ editYear }}년 자료가 없습니다. 입력 후 저장하면 새로 만듭니다.
      </p>
      <p v-else-if="fallback" class="tax-rate-manage-note">
        {{ sourceYear }}년 값이 표시 중입니다. 저장하면 {{ editYear }}년 자료가 생깁니다.
      </p>

      <div class="tax-rate-manage-grid">
        <label class="tax-rate-manage-field">
          <span class="tax-rate-manage-field__label">최저시급</span>
          <span class="tax-rate-manage-field__input">
            <input
              v-model="minHourly"
              type="text"
              inputmode="numeric"
              :disabled="loading || saving"
              @blur="blurMin"
            />
            <span class="tax-rate-manage-field__unit">원</span>
          </span>
        </label>
        <label
          v-for="field in rateFields"
          :key="field.key"
          class="tax-rate-manage-field"
        >
          <span class="tax-rate-manage-field__label">{{ field.label }}</span>
          <span class="tax-rate-manage-field__input">
            <input
              v-model="rates[field.key]"
              type="text"
              inputmode="decimal"
              :disabled="loading || saving"
              @blur="blurRate(field.key)"
            />
            <span class="tax-rate-manage-field__unit">%</span>
          </span>
        </label>
        <label class="tax-rate-manage-field">
          <span class="tax-rate-manage-field__label">인건비율</span>
          <span class="tax-rate-manage-field__input">
            <input
              v-model="laborCostRate"
              type="text"
              inputmode="decimal"
              :disabled="loading || saving"
              @blur="blurLabor"
            />
            <span class="tax-rate-manage-field__unit">%</span>
          </span>
        </label>
      </div>

      <div class="tax-rate-manage-actions">
        <button type="button" class="tax-rate-manage-btn tax-rate-manage-btn--save" :disabled="loading || saving" @click="save">
          {{ saving ? '저장 중' : '저장' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { commonWageApi } from '@/services/api';
import { formatWageNumber, parseWageNumber } from '@/composables/wage/useWageCalculator';
import {
  formatHomeWageRatePercent,
  parseHomeWageRatePercentInput,
} from '@/policies/homeWageSettingPolicy';
import type { CommonWageTaxRate } from '@/types/commonWage';

type RateKey = 'rateYeon' | 'rateGun' | 'rateGo' | 'rateJang' | 'rateSil' | 'rateSan';

const rateFields: { key: RateKey; label: string }[] = [
  { key: 'rateYeon', label: '국민연금' },
  { key: 'rateGun', label: '건강보험' },
  { key: 'rateGo', label: '고용보험' },
  { key: 'rateJang', label: '장기요양' },
  { key: 'rateSil', label: '실업급여' },
  { key: 'rateSan', label: '산재보험' },
];

const thisYear = new Date().getFullYear();
const editYear = ref(thisYear);
const yearItems = computed(() => {
  const years: number[] = [];
  for (let year = thisYear + 1; year >= thisYear - 4; year -= 1) years.push(year);
  return years;
});

const loading = ref(false);
const saving = ref(false);
const missing = ref(false);
const fallback = ref(false);
const sourceYear = ref<string | number | null>(null);
const minHourly = ref('');
const laborCostRate = ref('');
const rates = ref<Record<RateKey, string>>({
  rateYeon: '',
  rateGun: '',
  rateGo: '',
  rateJang: '',
  rateSil: '',
  rateSan: '',
});

function clearForm() {
  minHourly.value = '';
  laborCostRate.value = '';
  rates.value = {
    rateYeon: '',
    rateGun: '',
    rateGo: '',
    rateJang: '',
    rateSil: '',
    rateSan: '',
  };
}

function fill(data: CommonWageTaxRate) {
  minHourly.value = Number(data.minHourlyWage) > 0 ? formatWageNumber(Number(data.minHourlyWage)) : '';
  laborCostRate.value = formatHomeWageRatePercent(data.laborCostRate, {
    scaleFromDecimal: false,
    withSuffix: false,
  });
  const next = { ...rates.value };
  for (const field of rateFields) {
    next[field.key] = formatHomeWageRatePercent(data[field.key], { withSuffix: false });
  }
  rates.value = next;
}

async function load() {
  loading.value = true;
  try {
    const res = await commonWageApi.getTaxRate(editYear.value);
    const body = res.data as { success?: boolean; data?: CommonWageTaxRate; fallback?: boolean; message?: string };
    if (!body?.success || !body.data) {
      missing.value = true;
      fallback.value = false;
      sourceYear.value = null;
      clearForm();
      return;
    }
    missing.value = false;
    fallback.value = !!body.fallback;
    sourceYear.value = body.data.year;
    fill(body.data);
  } catch (error: unknown) {
    const err = error as { response?: { status?: number; data?: { message?: string } } };
    if (err.response?.status === 404) {
      missing.value = true;
      fallback.value = false;
      sourceYear.value = null;
      clearForm();
      return;
    }
    alert(err.response?.data?.message || '요율을 불러오지 못했습니다.');
  } finally {
    loading.value = false;
  }
}

function blurMin() {
  const amount = parseWageNumber(minHourly.value);
  minHourly.value = amount > 0 ? formatWageNumber(amount) : '';
}

function blurRate(key: RateKey) {
  const parsed = parseHomeWageRatePercentInput(rates.value[key]);
  rates.value = {
    ...rates.value,
    [key]: parsed != null ? formatHomeWageRatePercent(parsed, { withSuffix: false }) : '',
  };
}

function blurLabor() {
  const trimmed = laborCostRate.value.trim().replace(/%/g, '');
  if (!trimmed) {
    laborCostRate.value = '';
    return;
  }
  const amount = parseFloat(trimmed);
  laborCostRate.value = Number.isFinite(amount) ? String(amount) : '';
}

async function save() {
  if (saving.value || loading.value) return;

  const min = parseWageNumber(minHourly.value);
  if (min <= 0) {
    alert('최저시급을 확인해 주세요.');
    return;
  }

  const parsedRates: Record<RateKey, number> = {
    rateYeon: 0,
    rateGun: 0,
    rateGo: 0,
    rateJang: 0,
    rateSil: 0,
    rateSan: 0,
  };
  for (const field of rateFields) {
    const parsed = parseHomeWageRatePercentInput(rates.value[field.key]);
    if (parsed == null) {
      alert(`${field.label} 요율을 확인해 주세요.`);
      return;
    }
    parsedRates[field.key] = parsed;
  }

  const laborTrimmed = laborCostRate.value.trim().replace(/%/g, '');
  const labor = laborTrimmed ? parseFloat(laborTrimmed) : NaN;
  if (!Number.isFinite(labor) || labor < 0) {
    alert('인건비율을 확인해 주세요.');
    return;
  }

  saving.value = true;
  try {
    const res = await commonWageApi.saveTaxRate(editYear.value, {
      minHourlyWage: min,
      ...parsedRates,
      laborCostRate: labor,
    });
    const body = res.data as { success?: boolean; message?: string };
    if (!body?.success) {
      alert(body?.message || '저장에 실패했습니다.');
      return;
    }
    await load();
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } };
    alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
